import type {
  DinteroSessionCreateApiData,
  DinteroSessionCreateApiResponses,
  OrderCustomerGetApiData,
  OrderCustomerGetApiResponses,
  OrderDiscountApplyApiData,
  OrderDiscountApplyApiResponses,
  OrderDiscountRemoveApiData,
  OrderDiscountRemoveApiResponses,
  OrderGetApiData,
  OrderGetApiResponses,
  OrderLineDeleteApiData,
  OrderLineDeleteApiResponses,
  OrderLineDiscountEditApiData,
  OrderLineDiscountEditApiResponses,
  OrderLineDiscountGetApiData,
  OrderLineDiscountGetApiResponses,
  OrderLineListApiData,
  OrderLineListApiResponses,
  OrderListApiData,
  OrderListApiResponses,
  OrderPaymentSessionCreateApiData,
  OrderPaymentSessionCreateApiResponses,
  OrderRefundableListApiData,
  OrderRefundableListApiResponses,
  OrderRefundApiData,
  OrderRefundApiResponses,
  OrderRefundPreviewApiData,
  OrderRefundPreviewApiResponses,
  OrderUnpaidCreateInvoicesApiData,
  OrderUnpaidCreateInvoicesApiResponses,
  OrderUnpaidListApiData,
  OrderUnpaidListApiResponses,
  OrderUnpaidSumApiData,
  OrderUnpaidSumApiResponses,
  OrderUnpaidToExcelApiData,
  OrderUnpaidToExcelApiResponses,
  PaymentDocumentsDownloadApiData,
  PaymentDocumentsDownloadApiResponses,
  PaymentPosApiData,
  PaymentPosApiResponses,
  PosOrderResolveApiData,
  PosOrderResolveApiResponses
} from '@navio/nsdk';

import { downloadBlobInBrowser, getFilenameFromContentDisposition } from '../../browserBlobDownload';
import { downloadToExcel } from '../../downloadToExcel';
import type { ApiInput } from '../../utils';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';
import { paymentSessionAnalyticsBody, type PaymentSessionAnalyticsInput } from '../paymentSessionAnalytics';

export const getOrderUrls = {
  getPaginatedOrders: dataEndpoint<OrderListApiData, OrderListApiResponses>({
    getUrl: (queryParams) => apiUrl('orders/', queryParams)
  }),
  getOrderPartner: dataEndpoint<OrderGetApiData, OrderGetApiResponses>({
    getUrl: ({ orderId }) => apiUrl(`orders/${orderId}/`)
  }),
  getOrderCustomer: dataEndpoint<OrderCustomerGetApiData, OrderCustomerGetApiResponses>({
    getUrl: ({ orderId }) => apiUrl(`orders/${orderId}/customer/`)
  }),
  deleteOrderLine: dataEndpoint<OrderLineDeleteApiData, OrderLineDeleteApiResponses>({
    getUrl: ({ orderLineId }) => apiUrl(`order-lines/${orderLineId}/`),
    getBody: (data) => data
  }),
  getPaginatedOrderLines: dataEndpoint<OrderLineListApiData, OrderLineListApiResponses>({
    getUrl: (queryParams) => apiUrl('order-lines/', queryParams)
  }),
  postRefundOrderLinesInOrder: responseEndpoint<OrderRefundApiData, OrderRefundApiResponses>({
    getUrl: ({ orderId }) => apiUrl(`order/${orderId}/refund/`),
    getBody: (data) => data
  }),
  getPaginatedUnpaidOrders: dataEndpoint<OrderUnpaidListApiData, OrderUnpaidListApiResponses>({
    getUrl: (queryParams) => apiUrl('orders/unpaid/', queryParams)
  }),
  getUnpaidOrdersToExcel: dataEndpoint<OrderUnpaidToExcelApiData, OrderUnpaidToExcelApiResponses>({
    getUrl: (queryParams) => apiUrl('orders/unpaid/to-excel/', queryParams),
    handleRes: async (res) => downloadToExcel(res),
    responseType: 'blob',
    sideEffectGet: true
  }),
  postCreateInvoiceForUnpaidOrders: responseEndpoint<
    OrderUnpaidCreateInvoicesApiData,
    OrderUnpaidCreateInvoicesApiResponses
  >({
    getUrl: () => apiUrl('orders/unpaid/create-invoices/'),
    getBody: (data) => data
  }),
  getTotalUnpaidOrdersAmount: dataEndpoint<OrderUnpaidSumApiData, OrderUnpaidSumApiResponses>({
    getUrl: (queryParams) => apiUrl('orders/unpaid/sum/', queryParams)
  }),
  postDinteroSession: responseEndpoint<DinteroSessionCreateApiData, DinteroSessionCreateApiResponses>({
    getUrl: ({ orderId }) => apiUrl(`orders/${orderId}/create-dintero-session/`)
  }),
  // Provider-agnostic embedded payment session. Returns `provider` plus a
  // `sessionId`; Stripe additionally returns `clientSecret` + `publishableKey`
  // (+ optional `stripeAccountId`) for Embedded Checkout, Dintero re-embeds from
  // the id alone. Prefer this over `postDinteroSession`; branch on `provider`.
  // Body fields are ahead of nsdk until the BE OpenAPI publish lands; override Input.
  postOrderPaymentSession: responseEndpoint<
    OrderPaymentSessionCreateApiData,
    OrderPaymentSessionCreateApiResponses,
    ApiInput<OrderPaymentSessionCreateApiData> & PaymentSessionAnalyticsInput
  >({
    getUrl: ({ orderId }) => apiUrl(`orders/${orderId}/payment-session/`),
    getBody: paymentSessionAnalyticsBody
  }),
  postApplyDiscountToOrder: responseEndpoint<OrderDiscountApplyApiData, OrderDiscountApplyApiResponses>({
    getUrl: ({ orderId }) => apiUrl(`orders/${orderId}/discount/apply/`),
    getBody: (data) => data
  }),
  getOrderLineDiscount: dataEndpoint<OrderLineDiscountGetApiData, OrderLineDiscountGetApiResponses>({
    getUrl: ({ discountId }) => apiUrl(`order-line-discounts/${discountId}/`)
  }),
  patchOrderLineDiscount: responseEndpoint<OrderLineDiscountEditApiData, OrderLineDiscountEditApiResponses>({
    getUrl: ({ discountId }) => apiUrl(`order-line-discounts/${discountId}/`),
    getBody: ({ discountId: _, ...body }) => body
  }),
  removeOrderLineDiscount: responseEndpoint<OrderDiscountRemoveApiData, OrderDiscountRemoveApiResponses>({
    getUrl: ({ discountId }) => apiUrl(`order-line-discounts/${discountId}/remove/`)
  }),
  getPaginatedRefundableOrders: dataEndpoint<OrderRefundableListApiData, OrderRefundableListApiResponses>({
    getUrl: (queryParams) => apiUrl('orders/refundable/', queryParams)
  }),
  postRefundPreview: responseEndpoint<OrderRefundPreviewApiData, OrderRefundPreviewApiResponses>({
    getUrl: ({ orderId }) => apiUrl(`order/${orderId}/refund/preview/`),
    getBody: (data) => data
  }),
  postPosOrderFromPlate: responseEndpoint<PosOrderResolveApiData, PosOrderResolveApiResponses>({
    getUrl: ({ serviceDepartmentId }) => apiUrl(`orders/${serviceDepartmentId}/pos/resolve-from-plate/`),
    getBody: ({ serviceDepartmentId: _, ...body }) => body
  }),
  postPaymentPos: responseEndpoint<
    PaymentPosApiData,
    PaymentPosApiResponses,
    ApiInput<PaymentPosApiData> & PaymentSessionAnalyticsInput
  >({
    getUrl: ({ serviceDepartmentId, orderId }) => apiUrl(`orders/${serviceDepartmentId}/pos/${orderId}/payment/`),
    getBody: ({ serviceDepartmentId: _dept, orderId: _, ...body }) => body
  }),
  downloadOrderPaymentDocuments: dataEndpoint<PaymentDocumentsDownloadApiData, PaymentDocumentsDownloadApiResponses>({
    getUrl: ({ orderId }) => apiUrl(`payment-documents/${orderId}/download/`),
    responseType: 'blob',
    sideEffectGet: true,
    handleRes: async (res) => {
      const blob = res.data;
      const filename = getFilenameFromContentDisposition(res.headers['content-disposition'], 'payment-documents.pdf');
      downloadBlobInBrowser(blob, filename);
      return blob;
    }
  })
};
